import React, { useState } from 'react';
import { Plus, CheckSquare, FileText, X } from 'lucide-react';
import { Task } from '../../../store/slices/teamFocusSessionSlice';
import { Note } from '../../../store/slices/teamFocusSession/types';
import TasksTab from './TasksTab';
import NotesTab from './NotesTab';

interface RightSidebarProps {
    tasks: Task[];
    notes: Note[];
    tasksLoading: boolean;
    notesLoading: boolean;
    teamId: string;
    session: any;
    currentUserId: string;
    onToggleTask: (taskId: string) => void;
    onDeleteTask: (taskId: string) => void;
    onEditTask: (task: Task) => void;
    onDeleteNote: (noteId: string) => void;
    onEditNote: (note: Note) => void;
    onAddTask: () => void;
    onAddNote: () => void;
    isOpen?: boolean;
    onClose?: () => void;
}

type TabType = 'tasks' | 'notes';

const RightSidebar: React.FC<RightSidebarProps> = ({
    tasks,
    notes,
    tasksLoading,
    notesLoading,
    teamId,
    session,
    currentUserId,
    onToggleTask,
    onDeleteTask,
    onEditTask,
    onDeleteNote,
    onEditNote,
    onAddTask,
    onAddNote,
    isOpen,
    onClose,
}) => {
    const [activeTab, setActiveTab] = useState<TabType>('tasks');

    return (
        <>
            {/* Mobile Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={onClose}
                />
            )}

            <div
                className={`
                    w-80 sm:w-96 bg-white border-l border-gray-200/60 flex flex-col h-full shadow-sm shrink-0
                    lg:relative lg:translate-x-0
                    fixed top-0 right-0 bottom-0 z-50 transition-transform duration-300 ease-in-out
                    ${isOpen ? 'translate-x-0' : 'translate-x-full'}
                `}
            >
                {/* Tab Header */}
                <div className="border-b border-gray-200/60">
                    <div className="flex">
                        <button
                            onClick={() => setActiveTab('tasks')}
                            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${activeTab === 'tasks'
                                ? 'text-orange-600'
                                : 'text-gray-500 hover:text-gray-700'
                                } focus:outline-none`}
                        >
                            <div className="flex items-center justify-center gap-2">
                                <CheckSquare className="w-4 h-4" />
                                <span>Tasks</span>
                            </div>
                            {activeTab === 'tasks' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600" />
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('notes')}
                            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${activeTab === 'notes'
                                ? 'text-orange-600'
                                : 'text-gray-500 hover:text-gray-700'
                                } focus:outline-none`}
                        >
                            <div className="flex items-center justify-center gap-2">
                                <FileText className="w-4 h-4" />
                                <span>Notes</span>
                            </div>
                            {activeTab === 'notes' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600" />
                            )}
                        </button>
                        {/* Mobile Close Button */}
                        {onClose && (
                            <button
                                onClick={onClose}
                                className="lg:hidden w-16 py-3 text-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors flex items-center justify-center"
                                title="Close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto">
                    {activeTab === 'tasks' && (
                        <div className="p-4">
                            <TasksTab
                                tasks={tasks}
                                loading={tasksLoading}
                                session={session}
                                teamId={teamId}
                                onToggleTask={onToggleTask}
                                onDeleteTask={onDeleteTask}
                                onEditTask={onEditTask}
                            />
                        </div>
                    )}
                    {activeTab === 'notes' && (
                        <div className="p-4">
                            <NotesTab
                                notes={notes}
                                currentUserId={currentUserId}
                                onDeleteNote={onDeleteNote}
                                onEditNote={onEditNote}
                            />
                        </div>
                    )}
                </div>

                {/* Add Button */}
                <div className="p-4 border-t border-gray-200/60">
                    <button
                        onClick={activeTab === 'tasks' ? onAddTask : onAddNote}
                        className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-medium transition-colors shadow-sm"
                    >
                        <Plus className="w-4 h-4" />
                        <span>{activeTab === 'tasks' ? 'Add Task' : 'Add Note'}</span>
                    </button>
                </div>
            </div>
        </>
    );
};

export default RightSidebar;
